import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Loader2,
  Send,
  Radio,
  ChevronLeft,
  ChevronRight,
  Search,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  fetchAdminNotifications,
  sendNotificationsToUsers,
  broadcastNotificationAction,
  clearAdminNotificationError,
} from "../redux/slices/notificationSlice";
import UserPicker from "../components/users/UserPicker";
import { APP_SCREEN_LINKS, appScreenLabel } from "../lib/app-screen-links";
import {
  NOTIFICATION_TYPES,
  NOTIFICATION_PRIORITIES,
  notificationTypeLabel,
} from "../lib/notification-labels";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { useToast } from "../components/ui/use-toast";
import { formatDateTime } from "../lib/utils";

function Notifications() {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const {
    adminNotifications,
    adminPagination,
    adminLoading,
    adminError,
    sendBusy,
  } = useSelector((state) => state.notifications);

  const [page, setPage] = useState(1);
  const [listSearch, setListSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filterType, setFilterType] = useState("");
  const limit = 20;

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("announcement");
  const [priority, setPriority] = useState("normal");
  const [openInApp, setOpenInApp] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [dataJson, setDataJson] = useState("{}");

  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastMessage, setBroadcastMessage] = useState("");
  const [broadcastType, setBroadcastType] = useState("announcement");
  const [broadcastOpenInApp, setBroadcastOpenInApp] = useState("");

  useEffect(() => {
    dispatch(
      fetchAdminNotifications({
        page,
        limit,
        ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
        ...(filterType ? { type: filterType } : {}),
      })
    );
  }, [dispatch, page, appliedSearch, filterType]);

  useEffect(() => {
    if (adminError) {
      toast({
        title: "Notifications",
        description: adminError,
        variant: "destructive",
      });
      dispatch(clearAdminNotificationError());
    }
  }, [adminError, toast, dispatch]);

  const summarizeSendResults = (results) => {
    if (!Array.isArray(results)) return "";
    const ok = results.filter((r) => r.success).length;
    const fail = results.length - ok;
    const pushed = results.filter((r) => r.pushSent).length;
    const noToken = results.filter(
      (r) => r.pushSkippedReason === "no_fcm_token"
    ).length;
    const parts = [`${ok} saved`];
    if (pushed) parts.push(`${pushed} pushed to device`);
    if (noToken) {
      parts.push(
        `${noToken} had no device token (open the app while logged in)`
      );
    }
    if (fail) parts.push(`${fail} failed`);
    return parts.join(" · ");
  };

  const parsePayloadData = () => {
    if (!dataJson.trim() || dataJson.trim() === "{}") return {};
    try {
      const parsed = JSON.parse(dataJson);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      throw new Error("Advanced data must be valid JSON");
    }
  };

  const handleSendTargeted = async (e) => {
    e.preventDefault();
    if (!selectedUsers.length) {
      toast({
        title: "Choose recipients",
        description: "Search and select at least one user.",
        variant: "destructive",
      });
      return;
    }
    if (!title.trim() || !message.trim()) {
      toast({
        title: "Missing fields",
        description: "Title and message are required.",
        variant: "destructive",
      });
      return;
    }

    let data;
    try {
      data = parsePayloadData();
    } catch (err) {
      toast({
        title: "Invalid advanced data",
        description: err.message,
        variant: "destructive",
      });
      return;
    }

    try {
      const res = await dispatch(
        sendNotificationsToUsers({
          user_ids: selectedUsers.map((u) => u.id),
          title: title.trim(),
          message: message.trim(),
          type,
          priority,
          ...(openInApp ? { action_url: openInApp } : {}),
          data,
        })
      ).unwrap();

      toast({
        title: "Notifications sent",
        description: summarizeSendResults(res.results) || "Done.",
      });
      setSelectedUsers([]);
      dispatch(
        fetchAdminNotifications({
          page: 1,
          limit,
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(filterType ? { type: filterType } : {}),
        })
      );
      setPage(1);
    } catch (err) {
      toast({
        title: "Send failed",
        description: typeof err === "string" ? err : "Request failed",
        variant: "destructive",
      });
    }
  };

  const handleBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastTitle.trim() || !broadcastMessage.trim()) {
      toast({
        title: "Missing fields",
        description: "Title and message are required.",
        variant: "destructive",
      });
      return;
    }
    if (
      !window.confirm(
        "Send this message to every active user? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      const res = await dispatch(
        broadcastNotificationAction({
          title: broadcastTitle.trim(),
          message: broadcastMessage.trim(),
          type: broadcastType,
          ...(broadcastOpenInApp ? { action_url: broadcastOpenInApp } : {}),
        })
      ).unwrap();

      toast({
        title: "Broadcast sent",
        description: summarizeSendResults(res.results) || "Done.",
      });
      setBroadcastTitle("");
      setBroadcastMessage("");
      setBroadcastOpenInApp("");
      dispatch(
        fetchAdminNotifications({
          page: 1,
          limit,
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(filterType ? { type: filterType } : {}),
        })
      );
      setPage(1);
    } catch (err) {
      toast({
        title: "Broadcast failed",
        description: typeof err === "string" ? err : "Request failed",
        variant: "destructive",
      });
    }
  };

  const applyListSearch = () => {
    setPage(1);
    setAppliedSearch(listSearch);
  };

  const totalPages = Math.max(1, adminPagination.totalPages || 1);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Notifications</h1>
        <p className="text-muted-foreground">
          Send messages to learners in the app. Search by name or email — no
          technical IDs needed.
        </p>
      </div>

      <Tabs defaultValue="send" className="space-y-4">
        <TabsList>
          <TabsTrigger value="send" className="gap-2">
            <Send className="h-4 w-4" />
            Send to users
          </TabsTrigger>
          <TabsTrigger value="broadcast" className="gap-2">
            <Radio className="h-4 w-4" />
            Everyone
          </TabsTrigger>
          <TabsTrigger value="recent">History</TabsTrigger>
        </TabsList>

        <TabsContent value="send">
          <Card>
            <CardHeader>
              <CardTitle>Send to selected users</CardTitle>
              <CardDescription>
                They will see this in the app&apos;s notification inbox. Push
                alerts are sent when the user has the app installed.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSendTargeted} className="space-y-6">
                <div className="grid gap-2">
                  <Label>Who should receive this?</Label>
                  <UserPicker
                    selected={selectedUsers}
                    onChange={setSelectedUsers}
                    disabled={sendBusy}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-2 md:col-span-2">
                    <Label htmlFor="n-title">Title</Label>
                    <Input
                      id="n-title"
                      placeholder="e.g. New course available"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2 md:col-span-2">
                    <Label htmlFor="n-message">Message</Label>
                    <Textarea
                      id="n-message"
                      rows={4}
                      placeholder="Write the message learners will read…"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>Message style</Label>
                    <Select value={type} onValueChange={setType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NOTIFICATION_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label>Importance</Label>
                    <Select value={priority} onValueChange={setPriority}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {NOTIFICATION_PRIORITIES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            {p.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2 md:col-span-2">
                    <Label>When they tap the notification</Label>
                    <Select value={openInApp || "none"} onValueChange={(v) => setOpenInApp(v === "none" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a screen" />
                      </SelectTrigger>
                      <SelectContent>
                        {APP_SCREEN_LINKS.map((link) => (
                          <SelectItem
                            key={link.value || "none"}
                            value={link.value || "none"}
                          >
                            {link.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Optional. Opens that part of the mobile app when the user
                      taps the notification.
                    </p>
                  </div>
                </div>

                <div className="rounded-lg border">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50"
                    onClick={() => setShowAdvanced((v) => !v)}
                  >
                    Advanced options
                    {showAdvanced ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  {showAdvanced && (
                    <div className="border-t px-4 py-4 space-y-2">
                      <Label htmlFor="extra-json">
                        Extra data (JSON, for developers)
                      </Label>
                      <Textarea
                        id="extra-json"
                        className="font-mono text-sm"
                        rows={3}
                        value={dataJson}
                        onChange={(e) => setDataJson(e.target.value)}
                      />
                      <p className="text-xs text-muted-foreground">
                        Leave as {"{}"} unless your team asked for custom data.
                      </p>
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={sendBusy}>
                  {sendBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send notification
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="broadcast">
          <Card className="border-destructive/30">
            <CardHeader>
              <CardTitle>Message everyone</CardTitle>
              <CardDescription>
                Sends to all active accounts. Use only for important
                announcements (new course, maintenance, etc.).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBroadcast} className="space-y-4 max-w-xl">
                <div className="grid gap-2">
                  <Label htmlFor="b-title">Title</Label>
                  <Input
                    id="b-title"
                    value={broadcastTitle}
                    onChange={(e) => setBroadcastTitle(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="b-msg">Message</Label>
                  <Textarea
                    id="b-msg"
                    rows={4}
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                  />
                </div>
                <div className="grid gap-2">
                  <Label>Message style</Label>
                  <Select
                    value={broadcastType}
                    onValueChange={setBroadcastType}
                  >
                    <SelectTrigger className="max-w-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {NOTIFICATION_TYPES.filter((t) =>
                        ["announcement", "info", "warning"].includes(t.value)
                      ).map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label>When they tap the notification</Label>
                  <Select
                    value={broadcastOpenInApp || "none"}
                    onValueChange={(v) =>
                      setBroadcastOpenInApp(v === "none" ? "" : v)
                    }
                  >
                    <SelectTrigger className="max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_SCREEN_LINKS.map((link) => (
                        <SelectItem
                          key={link.value || "none"}
                          value={link.value || "none"}
                        >
                          {link.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" variant="destructive" disabled={sendBusy}>
                  {sendBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Radio className="h-4 w-4 mr-2" />
                  )}
                  Send to all active users
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="recent">
          <Card>
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle>Sent notifications</CardTitle>
                <CardDescription>
                  {adminPagination.total} message(s) on record
                </CardDescription>
              </div>
              <div className="flex flex-wrap gap-2 items-end">
                <div className="grid gap-1">
                  <span className="text-xs font-medium text-muted-foreground">
                    Style
                  </span>
                  <Select
                    value={filterType || "all"}
                    onValueChange={(v) => {
                      setPage(1);
                      setFilterType(v === "all" ? "" : v);
                    }}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All styles</SelectItem>
                      {NOTIFICATION_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Search title or message…"
                    value={listSearch}
                    onChange={(e) => setListSearch(e.target.value)}
                    className="w-[200px]"
                    onKeyDown={(e) =>
                      e.key === "Enter" &&
                      (e.preventDefault(), applyListSearch())
                    }
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={applyListSearch}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    dispatch(
                      fetchAdminNotifications({
                        page,
                        limit,
                        ...(appliedSearch.trim()
                          ? { search: appliedSearch.trim() }
                          : {}),
                        ...(filterType ? { type: filterType } : {}),
                      })
                    )
                  }
                >
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {adminLoading && adminNotifications.length === 0 ? (
                <div className="flex justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sent</TableHead>
                        <TableHead>Recipient</TableHead>
                        <TableHead>Style</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Opens</TableHead>
                        <TableHead>Read</TableHead>
                        <TableHead>Push</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adminNotifications.map((n) => (
                        <TableRow key={n.id}>
                          <TableCell className="whitespace-nowrap text-muted-foreground text-sm">
                            {n.created_at
                              ? formatDateTime(n.created_at)
                              : "—"}
                          </TableCell>
                          <TableCell className="max-w-[200px]">
                            <div className="truncate text-sm font-medium">
                              {n.user?.email || "Unknown user"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {notificationTypeLabel(n.type)}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[280px]">
                            <div className="font-medium truncate">{n.title}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {n.message}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">
                            {appScreenLabel(n.action_url)}
                          </TableCell>
                          <TableCell>
                            {n.is_read ? (
                              <Badge variant="secondary">Read</Badge>
                            ) : (
                              <Badge variant="outline">Unread</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {n.is_sent ? (
                              <Badge>Delivered</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                In-app only
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {!adminNotifications.length && !adminLoading ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">
                      No notifications match your filters.
                    </p>
                  ) : null}

                  <div className="flex items-center justify-between pt-6">
                    <p className="text-sm text-muted-foreground">
                      Page {page} of {totalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page <= 1 || adminLoading}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={
                          page >= totalPages ||
                          adminLoading ||
                          adminNotifications.length === 0
                        }
                        onClick={() =>
                          setPage((p) => (p >= totalPages ? p : p + 1))
                        }
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default Notifications;
